import {
  Alert,
  AlertIcon,
  AlertTitle,
  useColorModeValue,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { useCurrentUserQuery } from "features/User/hooks";
import { useMessageListQuery } from "features/Resources/Message/hooks";
import { useMessageTemplateListQuery } from "features/Resources/MessageTemplate/hooks";
import { createQueryString } from "helpers/queryString";
import { SHARED_ROUTES } from "definitions/constants/routeConstants";

export default function UnauthorizedMessageSenderBanner() {
  const { data: currentUser } = useCurrentUserQuery();
  const alertTextColor = useColorModeValue("fg.subtle", "inherit");
  const bg = useColorModeValue("blue.50", "blue.900");

  // Only query if user is not authorized
  const shouldQuery = !!currentUser && !currentUser.sendMessageAuthorized;

  // Build query string to filter by current user's senderId
  const queryString = useMemo(() => {
    if (!currentUser?.id) return "";
    return createQueryString({
      filters: [
        { fieldName: "sender_id", value: currentUser.id, operator: "=" },
      ],
    });
  }, [currentUser?.id]);

  // Query messages where user is sender with infinite stale time
  const { data: messages } = useMessageListQuery(queryString, {
    enabled: shouldQuery,
    staleTime: Infinity,
  });

  // Query templates where user is sender with infinite stale time
  const { data: templates } = useMessageTemplateListQuery(queryString, {
    enabled: shouldQuery,
    staleTime: Infinity,
  });

  // Calculate total count of messages + templates where user is sender
  const totalCount = useMemo(() => {
    const messageCount = messages?.length || 0;
    const templateCount = templates?.length || 0;
    return messageCount + templateCount;
  }, [messages, templates]);

  if (!shouldQuery || totalCount === 0) return null;

  const handleClick = () => {
    window.location.href = SHARED_ROUTES.individualSettings;
  };

  return (
    <Alert
      status="warning"
      cursor="pointer"
      onClick={handleClick}
      _hover={{ bg }}
      transition="background-color 0.2s"
    >
      <AlertIcon />
      <AlertTitle color={alertTextColor}>
        You have Message Templates/Messages where you are the sender, but you have not authorized Camino yet. Visit settings to authorize Camino.
      </AlertTitle>
    </Alert>
  );
}
